// Teams beyond the strip (shared/API.md §41.6, docs/PARITY.md P017 / P232 / P233):
// - TeamSheet: Android's TeamSwitcherSheet — every team with its icon, the current one checked, the
//   strip's badges — plus the current team's menu (Miembros del equipo, Agregar personas when the
//   server grants `add_user_to_team`, Salir del equipo) and «Ver todos los equipos» (the joinable
//   list). Selecting the current team is a no-op (Android).
// - TeamMembersDialog: `GET /users?in_team=` (the last answer cached in the worker for an instant
//   repaint), filtered locally while typing.
// - TeamAddDialog: people NOT in the team (`not_in_team` / `/users/search not_in_team_id`), several at
//   once, one `POST /teams/{id}/members/batch`.
// - JoinableTeamsDialog: `GET /teams` minus mine, open teams only → «Unirse».
// - LeaveTeamDialog: confirmation, then the worker wipes the team and the workspace moves on.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { AvatarIdentity } from '../../../core/avatarIdentity';
import { TeamDirectory } from '../../../core/teamDirectory';
import type { JoinableTeamsResult, TeamMember, TeamMembersResult } from '../../../data/features/nav';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, Dialog, EmptyState, InlineStatus, SearchInput } from '../../kit/Kit';
import { mediaUrls } from '../../media';
import type { TeamTile } from '../../models/sidebarModel';
import { useShell } from '../../shell/ShellContext';
import { openNav } from './navBus';
import '../people/people.css';
import './nav.css';

export const isMacPlatform = (): boolean => /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function TeamFace({ id, name, iconVersion, size = 36 }: { id: string; name: string; iconVersion: number; size?: number }) {
  return (
    <span className="nav-team-face" style={{ width: size, height: size }} aria-hidden>
      {iconVersion > 0 ? <img src={mediaUrls.teamIcon(id, iconVersion)} alt="" /> : AvatarIdentity.initials(name)}
    </span>
  );
}

function personName(m: TeamMember): string {
  const full = [m.firstName, m.lastName].filter(Boolean).join(' ');
  return full || m.nickname || m.username;
}

/** The team-scoped grants, asked once per open (unknown = hidden). */
function useTeamPermissions(teamId: string): string[] {
  const [perms, setPerms] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    void data.call('teamPermissions', teamId).then((p) => live && setPerms(p), () => undefined);
    return () => {
      live = false;
    };
  }, [teamId]);
  return perms;
}

export function TeamSheet({ teams, currentTeamId, onSelect, onClose }: { teams: TeamTile[]; currentTeamId: string; onSelect(teamId: string): void; onClose(): void }) {
  const perms = useTeamPermissions(currentTeamId);
  const current = teams.find((x) => x.team.id === currentTeamId) ?? null;
  const mac = isMacPlatform();
  const go = (request: Parameters<typeof openNav>[0]) => {
    onClose();
    openNav(request);
  };
  return (
    <Dialog title={t('teams.title')} onClose={onClose} width={440}>
      <div className="nav-team-sheet">
        <div className="people-list" role="listbox" aria-label={t('web.teams.switch')}>
          {teams.map((tile, index) => {
            const isCurrent = tile.team.id === currentTeamId;
            return (
              <button
                type="button"
                key={tile.team.id}
                role="option"
                aria-selected={isCurrent}
                className={`list-row clickable people-row nav-team-row${isCurrent ? ' current' : ''}${tile.isMuted ? ' muted' : ''}`}
                data-team-id={tile.team.id}
                onClick={() => {
                  onClose();
                  if (!isCurrent) onSelect(tile.team.id);
                }}
              >
                <span className="list-row-leading">
                  <TeamFace id={tile.team.id} name={tile.team.displayName} iconVersion={tile.team.lastTeamIconUpdate} />
                </span>
                <span className="list-row-text">
                  <span className="list-row-title">{tile.team.displayName}</span>
                  {tile.team.description ? <span className="list-row-subtitle">{tile.team.description}</span> : null}
                </span>
                <span className="list-row-trailing nav-team-trailing">
                  {tile.mentionCount > 0 ? <span className="badge">{tile.mentionCount > 99 ? '99+' : tile.mentionCount}</span> : tile.hasUnread ? <span className="nav-dot" /> : null}
                  {index < 9 ? <kbd className="nav-kbd">{tf(mac ? 'web.teams.shortcutMac' : 'web.teams.shortcut', index + 1)}</kbd> : null}
                  {isCurrent ? <Icon name="check" size={16} /> : <span style={{ width: 16 }} />}
                </span>
              </button>
            );
          })}
        </div>
        {current ? (
          <div className="nav-team-actions" role="group" aria-label={current.team.displayName}>
            <div className="nav-section-title">{current.team.displayName}</div>
            <button type="button" className="menu-item" onClick={() => go({ kind: 'teamMembers', teamId: currentTeamId })}>
              <Icon name="people" size={16} /> {t('web.teams.members')}
            </button>
            {perms.includes('add_user_to_team') ? (
              <button type="button" className="menu-item" data-testid="team-add-people" onClick={() => go({ kind: 'teamAdd', teamId: currentTeamId })}>
                <Icon name="plus" size={16} /> {t('web.teams.addPeople')}
              </button>
            ) : null}
            <button type="button" className="menu-item danger" onClick={() => go({ kind: 'teamLeave', teamId: currentTeamId })}>
              <Icon name="signOut" size={16} /> {t('web.teams.leave')}
            </button>
          </div>
        ) : null}
        <button type="button" className="nav-text-action" onClick={() => go({ kind: 'teamsJoinable' })}>
          {t('web.teams.seeAll')}
        </button>
      </div>
    </Dialog>
  );
}

/** The team menu's extra entries for one team (the strip tile's right-click). */
export function TeamMenuItems({ teamId, onDone }: { teamId: string; onDone(): void }) {
  const perms = useTeamPermissions(teamId);
  const go = (request: Parameters<typeof openNav>[0]) => {
    onDone();
    openNav(request);
  };
  return (
    <>
      <button className="menu-item" role="menuitem" onClick={() => go({ kind: 'teamMembers', teamId })}>
        <Icon name="people" size={16} /> {t('web.teams.members')}
      </button>
      {perms.includes('add_user_to_team') ? (
        <button className="menu-item" role="menuitem" onClick={() => go({ kind: 'teamAdd', teamId })}>
          <Icon name="plus" size={16} /> {t('web.teams.addPeople')}
        </button>
      ) : null}
      <button className="menu-item" role="menuitem" onClick={() => go({ kind: 'teamsJoinable' })}>
        <Icon name="stack" size={16} /> {t('web.teams.seeAll')}
      </button>
      <button className="menu-item danger" role="menuitem" onClick={() => go({ kind: 'teamLeave', teamId })}>
        <Icon name="signOut" size={16} /> {t('web.teams.leave')}
      </button>
    </>
  );
}

function MemberRow({ m, trailing, selected, onClick }: { m: TeamMember; trailing?: React.ReactNode; selected?: boolean; onClick?(): void }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`list-row people-row${onClick ? ' clickable' : ''}${selected ? ' selected' : ''}`} onClick={onClick} data-user-id={m.id}>
      <span className="list-row-leading">
        <Avatar userId={m.id} name={personName(m)} size={32} version={m.lastPictureUpdate} />
      </span>
      <span className="list-row-text">
        <span className="list-row-title">
          {personName(m)}
          {m.isBot ? <span className="people-badge">BOT</span> : null}
        </span>
        <span className="list-row-subtitle people-handle">@{m.username}</span>
      </span>
      {trailing ? <span className="list-row-trailing">{trailing}</span> : null}
    </Tag>
  );
}

export function TeamMembersDialog({ teamId, onClose }: { teamId: string; onClose(): void }) {
  const shell = useShell();
  const teamName = shell.teams.find((x) => x.id === teamId)?.displayName ?? '';
  const [result, setResult] = useState<TeamMembersResult | null>(null);
  const [query, setQuery] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    // The last listing paints at once; the server's answer replaces it.
    void data.call('teamMembersCached', teamId).then((cached) => live && cached && setResult((r) => r ?? { members: cached }));
    void data.call('teamMembers', teamId).then(
      (r) => live && setResult((prev) => ('error' in r && prev && !('error' in prev) ? prev : r)),
      () => live && setResult((prev) => prev ?? { error: 'network' }),
    );
    return () => {
      live = false;
    };
  }, [teamId, tick]);
  const members = result && !('error' in result) ? result.members : null;
  const visible = useMemo(() => (members ?? []).filter((m) => TeamDirectory.matchesPerson(query, [personName(m), m.username, m.nickname])), [members, query]);
  const count = members ? (members.length === 1 ? t('web.teams.membersCountOne') : tf('web.teams.membersCount', members.length)) : '';
  return (
    <Dialog title={`${t('web.teams.members')} · ${teamName}`} onClose={onClose} width={520}>
      <div className="people-dialog-root">
        <div className="people-sticky">
          <SearchInput value={query} onChange={setQuery} placeholder={t('web.teams.membersSearch')} autoFocus />
          {count ? <span className="nav-muted nav-count">{count}</span> : null}
        </div>
        {result === null ? <InlineStatus kind="loading" text={t('web.people.searching')} /> : null}
        {result && 'error' in result ? <InlineStatus kind="error" text={t('web.teams.loadFailed')} onRetry={() => setTick((n) => n + 1)} /> : null}
        {members && !visible.length && query.trim() ? <EmptyState title={tf('web.teams.noMatches', query.trim())} /> : null}
        <div className="people-list" role="list">
          {visible.map((m) => (
            <div role="listitem" key={m.id}>
            <MemberRow
              m={m}
              onClick={
                m.id === shell.me.id
                  ? undefined
                  : () => {
                      onClose();
                      shell.openDirectMessage(m.id);
                    }
              }
            />
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

export function TeamAddDialog({ teamId, onClose }: { teamId: string; onClose(): void }) {
  const shell = useShell();
  const teamName = shell.teams.find((x) => x.id === teamId)?.displayName ?? '';
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<TeamMembersResult | null>(null);
  const [picked, setPicked] = useState<Map<string, TeamMember>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const timer = setTimeout(
      () => {
        void data.call('teamAddCandidates', teamId, query).then(
          (r) => live && setResult(r),
          () => live && setResult({ error: 'network' }),
        );
      },
      query.trim() ? 250 : 0,
    );
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [teamId, query]);
  const candidates = result && !('error' in result) ? result.members : null;
  const toggle = (m: TeamMember) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(m.id)) next.delete(m.id);
      else next.set(m.id, m);
      return next;
    });
  const add = async () => {
    if (!picked.size || busy) return;
    setBusy(true);
    setError(null);
    const ok = await data.call('teamAddMembers', teamId, [...picked.keys()]).catch(() => false);
    setBusy(false);
    if (!ok) return setError(t('web.teams.addFailed'));
    shell.toast(picked.size === 1 ? t('web.teams.addedOne') : tf('web.teams.added', picked.size));
    onClose();
  };
  const footer = (
    <>
      <span className={`people-footer-hint${error ? ' error' : ''}`} role={error ? 'alert' : undefined}>
        {error ?? ''}
      </span>
      <Button kind="quiet" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button kind="primary" disabled={!picked.size || busy} onClick={() => void add()}>
        {picked.size ? tf('web.teams.addCount', picked.size) : t('web.teams.add')}
      </Button>
    </>
  );
  return (
    <Dialog title={`${t('web.teams.addPeople')} · ${teamName}`} onClose={onClose} footer={footer} width={520}>
      <div className="people-dialog-root">
        <div className="people-sticky">
          <SearchInput value={query} onChange={setQuery} placeholder={t('web.teams.addSearch')} autoFocus />
        </div>
        {result === null ? <InlineStatus kind="loading" text={t('web.people.searching')} /> : null}
        {result && 'error' in result ? <InlineStatus kind="error" text={t('web.teams.loadFailed')} /> : null}
        {candidates && !candidates.length ? <EmptyState title={query.trim() ? tf('web.teams.noMatches', query.trim()) : t('web.teams.addEmpty')} /> : null}
        <div className="people-list" role="listbox" aria-multiselectable>
          {(candidates ?? []).map((m) => (
            <MemberRow key={m.id} m={m} selected={picked.has(m.id)} onClick={() => toggle(m)} trailing={<span className="people-check">{picked.has(m.id) ? <Icon name="check" size={12} strokeWidth={3} /> : null}</span>} />
          ))}
        </div>
      </div>
    </Dialog>
  );
}

export function JoinableTeamsDialog({ onJoined, onClose }: { onJoined(teamId: string): void; onClose(): void }) {
  const [result, setResult] = useState<JoinableTeamsResult | null>(null);
  const [joining, setJoining] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setResult(null);
    void data.call('joinableTeams').then(
      (r) => live && setResult(r),
      () => live && setResult({ error: 'network' }),
    );
    return () => {
      live = false;
    };
  }, [tick]);
  const join = async (teamId: string) => {
    if (joining) return;
    setJoining(teamId);
    setError(null);
    const ok = await data.call('joinTeam', teamId).catch(() => false);
    setJoining(null);
    if (!ok) return setError(t('web.teams.joinFailed'));
    onClose();
    onJoined(teamId);
  };
  const teams = result && !('error' in result) ? result.teams : null;
  return (
    <Dialog
      title={t('web.teams.seeAll')}
      onClose={onClose}
      width={480}
      footer={
        error ? (
          <span className="people-footer-hint error" role="alert">
            {error}
          </span>
        ) : undefined
      }
    >
      {result === null ? <InlineStatus kind="loading" text={t('web.people.searching')} /> : null}
      {result && 'error' in result ? <InlineStatus kind="error" text={t('web.teams.loadFailed')} onRetry={() => setTick((n) => n + 1)} /> : null}
      {teams && !teams.length ? <EmptyState title={t('web.teams.joinableEmpty')} /> : null}
      <div className="people-list" role="list">
        {(teams ?? []).map((team) => (
          <div key={team.id} role="listitem" className="list-row people-row" data-team-id={team.id}>
            <span className="list-row-leading">
              <TeamFace id={team.id} name={team.displayName} iconVersion={team.lastTeamIconUpdate} />
            </span>
            <span className="list-row-text">
              <span className="list-row-title">{team.displayName}</span>
              {team.description ? <span className="list-row-subtitle">{team.description}</span> : null}
            </span>
            <span className="list-row-trailing">
              <button className="people-trailing-action" disabled={joining !== null} onClick={() => void join(team.id)}>
                {joining === team.id ? '…' : t('web.teams.join')}
              </button>
            </span>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

export function LeaveTeamDialog({ teamId, teamName, onLeft, onClose }: { teamId: string; teamName: string; onLeft(): void; onClose(): void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leave = async () => {
    setBusy(true);
    setError(null);
    const ok = await data.call('leaveTeam', teamId).catch(() => false);
    setBusy(false);
    if (!ok) return setError(t('web.teams.leaveFailed'));
    onClose();
    onLeft();
  };
  return (
    <Dialog
      title={tf('web.teams.leaveTitle', teamName)}
      onClose={onClose}
      width={420}
      footer={
        <>
          {error ? (
            <span className="people-footer-hint error" role="alert">
              {error}
            </span>
          ) : null}
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="danger" disabled={busy} onClick={() => void leave()}>
            {busy ? '…' : t('web.teams.leave')}
          </Button>
        </>
      }
    >
      <p className="nav-confirm">{t('web.teams.leaveMessage')}</p>
    </Dialog>
  );
}
